import React from 'react';
import { Globe } from 'lucide-react';
import { motion } from 'framer-motion';
import { StarRating } from '@/components/StarRating';
import { fadeUp, revealOnce } from '@/lib/motion';
import { useLanguage } from '@/hooks/useLanguage';

function TestimonialCard({ testimonial, index }) {
  return (
    <motion.div
      {...revealOnce}
      variants={fadeUp}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="bg-cream/30 rounded-lg p-6 border border-saffron-100/30 hover:shadow-card-lift transition-shadow duration-300"
      data-testid={`testimonial-card-${index}`}
    >
      <div className="flex gap-0.5 mb-3">
        <StarRating rating={testimonial.rating} size={14} />
      </div>
      <p className="text-sm text-earth/70 font-sans leading-relaxed mb-4 italic">"{testimonial.text}"</p>
      <div className="border-t border-saffron-100/30 pt-3">
        <p className="font-display text-sm font-bold text-earth-dark">{testimonial.name}</p>
        <p className="text-[11px] text-earth/50 font-sans">{testimonial.company}</p>
        <div className="flex items-center gap-1 mt-1">
          <Globe size={10} className="text-saffron/50" />
          <span className="text-[10px] text-earth/40 font-sans">{testimonial.country}</span>
        </div>
      </div>
    </motion.div>
  );
}

export default function TestimonialsSection({ testimonials }) {
  const { t } = useLanguage();
  if (testimonials.length === 0) return null;
  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white" data-testid="testimonials-section">
      <div className="max-w-6xl mx-auto">
        <motion.div {...revealOnce} variants={fadeUp} transition={{ duration: 0.6 }} className="text-center mb-14">
          <span className="text-xs uppercase tracking-[0.3em] text-saffron font-sans font-semibold">{t.testimonials.subtitle}</span>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-earth-dark tracking-tight mt-3">{t.testimonials.title}</h2>
          <div className="w-16 h-0.5 bg-saffron mx-auto mt-4" />
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {testimonials.slice(0, 4).map((item, idx) => (
            <TestimonialCard key={item.id} testimonial={item} index={idx} />
          ))}
        </div>
      </div>
    </section>
  );
}
